import React, { useState, useEffect, ReactNode, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import SharedData from '../common/SharedData';
import Loader from '../common/Loader';
import NormalMessage from '../common/NormalMessage';
import { getStockReport } from './core/_requests';
import { StockItem } from './stock/core/_models';
import StockReportFilters from './stock/StockReportFilters';
import StockReportTable from './stock/StockReportTable';
import { prepareFilters } from '../orders/core/_requests';
import Paginator from '../common/Paginator';

export const StockReport: React.FC = () => {
    const { formatMessage } = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentUser, language } = SharedData();

    const [dataList, setDataList] = useState<StockItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [totalQuantity, setTotalQuantity] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    
    const [currentPage, setCurrentPage] = useState<number>(parseInt(searchParams.get('current_page') || '1', 10));
    const [perPage, setPerPage] = useState<number>(parseInt(searchParams.get('limit') || '20', 10));

    const fetchData = useCallback(async () => {
            if (!currentUser?.token) {
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                const apiSearchParams = new URLSearchParams(searchParams.toString());
                
                if (searchParams.has('current_page')) {
                    apiSearchParams.set('page', searchParams.get('current_page') || '1');
                    apiSearchParams.delete('current_page');
                }
                
                const res = await getStockReport('?' + apiSearchParams.toString(), currentUser.token, language);
                const stockData = res.data.stock || [];
                setDataList(stockData);
   
                const rowCount = res.data.count || 0;
                setTotalItems(rowCount);
                
            
                const totalQty = Number(res.data.total) || 0;
                setTotalQuantity(totalQty);
                
     
                if (res.data.page) {
                    setCurrentPage(Number(res.data.page));
                }
                if (res.data.limit) {
                    setPerPage(Number(res.data.limit));
                }
            } catch (error) {
                console.error("Error fetching stock report:", error);
                setDataList([]);
                setTotalItems(0);
            } finally {
                setIsLoading(false);
            }
    }, [currentUser, language, searchParams, setSearchParams]);

    const refreshData = useCallback(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        prepareFilters(searchParams, setSearchParams);
        fetchData();
    }, [searchParams, currentUser, language, setSearchParams, fetchData]);

    const totalPages = Math.ceil(totalItems / perPage);

    const renderPagination = (): ReactNode => {
        if (totalItems <= 0 || totalPages <= 1) {
            return null;
        }

        return (
            <div className='d-flex justify-content-center mt-5'>
                <Paginator
                    totalResult={totalItems}
                    perPage={perPage}
                />
            </div>
        );
    };

    const renderResults = (): ReactNode => {
        if (isLoading) {
            return <Loader />;
        }
        if (dataList.length > 0) {
            return <StockReportTable 
                data={dataList} 
                isLoading={isLoading} 
                totalItems={totalQuantity}
                refreshData={refreshData}
            />;
        }
        return <NormalMessage msg={formatMessage({ id: "no_data" })} />;
    }

    return (
        <div className='card'>
            <StockReportFilters />

            <div className='card-body py-3'>
                {renderResults()}
                {renderPagination()}
            </div>
        </div>
    );
};
